// Sonderteile-Anfragen: Ein Sachbearbeiter braucht für eine Kunden-Anfrage
// ein zugekauftes Teil und muss zuerst selbst Preis/Liefertermin beim
// Lieferanten erfragen — das ist NICHT Jasmins Aufgabe. Erst wenn daraus
// eine Bestellung wird, gibt ein Klick sie an den Einkauf weiter.
import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, type SonderteilAnfrage, type TicketDatei, type Lieferant } from '../lib/api';
import { useEntwurf, useEntwurfKey } from '../hooks/useEntwurf';
import { EntwurfBanner } from '../components/EntwurfBanner';

type Entwurf = Partial<SonderteilAnfrage>;

interface Kontakt { email: string | null; ansprechpartner?: string | null; telefon?: string | null }

function mailtoLink(kontakt: Kontakt, was: string, dvNummer?: string | null, anhaengeCount = 0): string | null {
  if (!kontakt.email) return null;
  const anrede = kontakt.ansprechpartner ? `Hallo ${kontakt.ansprechpartner},` : 'Hallo,';
  const body = [
    anrede, '',
    `könnten Sie uns bitte ein Angebot (Preis + Liefertermin) für folgendes Teil machen:`, '',
    was,
    dvNummer ? `(Bezug: ${dvNummer})` : '',
    '', 'Vielen Dank und viele Grüße',
    anhaengeCount > 0 ? `\n⚠ NICHT VERGESSEN: ${anhaengeCount} Anhang/Anhänge (Zeichnung/Datenblatt) sind hier NICHT automatisch angehängt — bitte manuell aus der Anfrage herunterladen und hier anhängen!` : '',
  ].filter(Boolean).join('\n');
  const subject = `Anfrage: ${was}`;
  return `mailto:${encodeURIComponent(kontakt.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function Sonderteile() {
  const list = useApi(() => api.sonderteile.list(), []);
  const kunden = useApi(() => api.lookup.kunden(), []);
  const lieferanten = useApi(() => api.lieferanten.list(), []);
  const me = useApi(() => api.dashboard.me(), []);
  const meineId = me.data?.user.id;
  const [neu, setNeu] = useState<Entwurf | null>(null);
  const [busy, setBusy] = useState<number | 'neu' | null>(null);
  // Entwurf (Autosave): Neue Anfrage geht nicht verloren, wenn jemand ohne Anlegen den PC verlässt
  const ent = useEntwurf<Entwurf>(useEntwurfKey('sonderteile'), neu);

  const speichern = async () => {
    if (!neu?.was?.trim()) return;
    setBusy('neu');
    try {
      await api.sonderteile.create({ ...neu, was: neu.was.trim() });
      setNeu(null);
      ent.nachSpeichern();
      list.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(null);
  };

  const speichernFeld = async (row: SonderteilAnfrage, patch: Partial<SonderteilAnfrage>) => {
    setBusy(row.id);
    try { await api.sonderteile.update(row.id, patch); list.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(null);
  };

  const bestellen = async (row: SonderteilAnfrage) => {
    if (!confirm(`"${row.was}" an den Einkauf weiterleiten? Jasmin sieht es dann in ihrer Aufgabenliste.`)) return;
    setBusy(row.id);
    try { await api.sonderteile.bestellen(row.id); list.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(null);
  };

  const loeschen = async (id: number) => {
    if (!confirm('Anfrage löschen?')) return;
    try { await api.sonderteile.remove(id); list.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  const rows = list.data?.data ?? [];
  const offen = rows.filter(r => r.status === 'offen');
  const bestellt = rows.filter(r => r.status === 'bestellt');

  return (
    <div className="p-8 max-w-5xl">
      <header className="mb-4 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
          <h1 className="text-2xl font-bold text-slate-900">Sonderteile-Anfragen</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Für Kunden-Anfragen, die ein zugekauftes Sonderteil brauchen: Preis und Liefertermin selbst beim
            Lieferanten erfragen. Für alle Sachbearbeiter sichtbar — Jasmin sieht nichts, bis du auf „An Einkauf weiterleiten" klickst.
          </p>
        </div>
        <button onClick={() => { setNeu({}); ent.beimOeffnen({}); }} className="bg-brand text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 whitespace-nowrap">
          + Neue Anfrage
            {ent.entwurf && !neu && (
              <span className="ml-2 rounded-full bg-amber-300 text-amber-950 text-[11px] font-semibold px-2 py-0.5"
                    title="Du hast einen nicht gespeicherten Entwurf — er liegt nur in diesem Browser">📝 Entwurf</span>
            )}
        </button>
      </header>

      {neu && ent.wartet && ent.entwurf && (
        <EntwurfBanner className="mb-3" at={ent.entwurf.at}
          zusammenfassung={[ent.entwurf.data.was, ent.entwurf.data.dv_nummer].filter(Boolean).join(' · ') || undefined}
          onWiederherstellen={() => { const d = ent.wiederherstellen(); if (d) setNeu({ ...d }); }}
          onVerwerfen={ent.verwerfen} />
      )}

      {neu && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-2">
          <input className="input md:col-span-2" placeholder="Was wird gebraucht? *" value={neu.was ?? ''}
                 onChange={e => setNeu({ ...neu, was: e.target.value })} autoFocus />
          <select className="input" value={neu.kunde_id ?? ''} onChange={e => setNeu({ ...neu, kunde_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Kunde (optional)</option>
            {kunden.data?.data.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          <input className="input" placeholder="DV-Nr / Auftrag (optional)" value={neu.dv_nummer ?? ''}
                 onChange={e => setNeu({ ...neu, dv_nummer: e.target.value })} />
          <select className="input" value={neu.lieferant_id ?? ''}
                  onChange={e => setNeu({ ...neu, lieferant_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Lieferant (aus Datenbank)</option>
            {lieferanten.data?.data.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <input className="input" placeholder="…oder Lieferant manuell eintragen" value={neu.angefragt_bei ?? ''}
                 onChange={e => setNeu({ ...neu, angefragt_bei: e.target.value })} disabled={!!neu.lieferant_id} />
          <input className="input" placeholder="Kommentar" value={neu.kommentar ?? ''}
                 onChange={e => setNeu({ ...neu, kommentar: e.target.value })} />
          {!neu.lieferant_id && neu.angefragt_bei && (
            <input className="input" type="email" placeholder="E-Mail des manuellen Lieferanten (für Mail-Button)"
                   value={neu.angefragt_bei_email ?? ''}
                   onChange={e => setNeu({ ...neu, angefragt_bei_email: e.target.value })} />
          )}
          {neu.lieferant_id && (() => {
            const l = lieferanten.data?.data.find(x => x.id === neu.lieferant_id);
            if (!l) return null;
            const link = mailtoLink(l, neu.was || '(Teil noch nicht benannt)', neu.dv_nummer);
            return (
              <div className="col-span-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2">
                <div>
                  {l.ansprechpartner && <>👤 {l.ansprechpartner} · </>}
                  {l.email ? <>✉ {l.email}</> : <span className="text-amber-600">Keine E-Mail hinterlegt</span>}
                  {l.telefon && <> · ☎ {l.telefon}</>}
                </div>
                {link && <a href={link} className="text-brand hover:underline font-medium whitespace-nowrap">✉ E-Mail senden</a>}
              </div>
            );
          })()}
          <div className="col-span-full flex gap-2 justify-end">
            <button onClick={() => setNeu(null)} className="btn-secondary">Abbrechen</button>
            <button onClick={() => void speichern()} disabled={busy === 'neu' || !neu.was?.trim()}
                    className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-40">
              {busy === 'neu' ? 'Speichert …' : 'Anlegen'}
            </button>
          </div>
        </div>
      )}

      {list.loading && <div className="text-slate-500">Lädt …</div>}

      {offen.length > 0 && (
        <div className="mb-6">
          <h2 className="font-semibold text-slate-900 mb-2">Offen — warte auf Preis/Liefertermin ({offen.length})</h2>
          <div className="space-y-2">
            {offen.map(r => (
              <SonderteilCard key={r.id} row={r} busy={busy === r.id}
                               istEigene={meineId === undefined || r.created_by_id === meineId}
                               lieferant={lieferanten.data?.data.find(l => l.id === r.lieferant_id)}
                               onSave={patch => speichernFeld(r, patch)}
                               onBestellen={() => bestellen(r)}
                               onDelete={() => loeschen(r.id)} />
            ))}
          </div>
        </div>
      )}

      {bestellt.length > 0 && (
        <div>
          <h2 className="font-semibold text-slate-900 mb-2">An Einkauf weitergeleitet ({bestellt.length})</h2>
          <div className="space-y-2">
            {bestellt.map(r => (
              <div key={r.id} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-800">{r.was}</div>
                  <div className="text-xs text-slate-500">
                    {r.ersteller_name && <>von {r.ersteller_name} · </>}
                    {r.kunde_name && <>Kunde: {r.kunde_name} · </>}
                    Weitergeleitet am {r.bestellt_am ? new Date(r.bestellt_am).toLocaleDateString('de-DE') : '—'}
                  </div>
                </div>
                <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap">✓ Bei Jasmin</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!list.loading && rows.length === 0 && (
        <div className="text-slate-400 text-sm">Noch keine Sonderteile-Anfragen.</div>
      )}
    </div>
  );
}

function SonderteilCard({ row, busy, istEigene, lieferant, onSave, onBestellen, onDelete }: {
  row: SonderteilAnfrage; busy: boolean; istEigene: boolean; lieferant?: Lieferant;
  onSave: (patch: Partial<SonderteilAnfrage>) => void;
  onBestellen: () => void;
  onDelete: () => void;
}) {
  const [preis, setPreis] = useState(row.preis != null ? String(row.preis) : '');
  const [liefertermin, setLiefertermin] = useState(row.liefertermin ?? '');
  const [manuellEmail, setManuellEmail] = useState(row.angefragt_bei_email ?? '');
  const dateien = useApi(() => api.sonderteile.dateien(row.id), [row.id]);
  const anhaengeCount = dateien.data?.data.length ?? 0;
  const kontakt: Kontakt | null = lieferant ?? (row.angefragt_bei ? { email: row.angefragt_bei_email, ansprechpartner: null, telefon: null } : null);
  const link = kontakt ? mailtoLink(kontakt, row.was, row.dv_nummer, anhaengeCount) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-medium text-slate-800">{row.was}</div>
          <div className="text-xs text-slate-500">
            {row.ersteller_name && <>von <span className="font-medium text-slate-600">{row.ersteller_name}</span> · </>}
            {row.kunde_name && <>Kunde: {row.kunde_name} · </>}
            {row.dv_nummer && <>{row.dv_nummer} · </>}
            {(row.lieferant_name || row.angefragt_bei) && <>angefragt bei {row.lieferant_name ?? row.angefragt_bei}</>}
          </div>
          {row.kommentar && <div className="text-xs text-slate-500 mt-1">💬 {row.kommentar}</div>}
        </div>
        {istEigene && <button onClick={onDelete} className="text-slate-300 hover:text-red-600 text-xs shrink-0">✕</button>}
      </div>
      {lieferant && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            {lieferant.ansprechpartner && <>👤 {lieferant.ansprechpartner} · </>}
            {lieferant.email ? <>✉ {lieferant.email}</> : <span className="text-amber-600">Keine E-Mail hinterlegt</span>}
            {lieferant.telefon && <> · ☎ {lieferant.telefon}</>}
          </div>
          {link && <a href={link} className="text-brand hover:underline font-medium whitespace-nowrap">✉ E-Mail senden</a>}
        </div>
      )}
      {!lieferant && row.angefragt_bei && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span>👤 {row.angefragt_bei} ·</span>
            {istEigene ? (
              <input className="input w-56 text-xs py-1" type="email" placeholder="E-Mail eintragen …" value={manuellEmail}
                     onChange={e => setManuellEmail(e.target.value)}
                     onBlur={() => onSave({ angefragt_bei_email: manuellEmail || null })} />
            ) : (
              row.angefragt_bei_email ? <>✉ {row.angefragt_bei_email}</> : <span className="text-amber-600">Keine E-Mail hinterlegt</span>
            )}
          </div>
          {link && <a href={link} className="text-brand hover:underline font-medium whitespace-nowrap">✉ E-Mail senden</a>}
        </div>
      )}
      {istEigene ? (
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Preis (€)</label>
            <input className="input w-28" type="number" step="0.01" value={preis}
                   onChange={e => setPreis(e.target.value)}
                   onBlur={() => onSave({ preis: preis ? Number(preis) : null })} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Liefertermin (Datum)</label>
            <input type="date" className="input w-40"
                   value={/^\d{4}-\d{2}-\d{2}$/.test(liefertermin) ? liefertermin : ''}
                   onChange={e => { setLiefertermin(e.target.value); onSave({ liefertermin: e.target.value || null }); }} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">…oder KW / Freitext</label>
            <input className="input w-32" placeholder="z.B. KW 34"
                   value={/^\d{4}-\d{2}-\d{2}$/.test(liefertermin) ? '' : liefertermin}
                   onChange={e => setLiefertermin(e.target.value)}
                   onBlur={() => onSave({ liefertermin: liefertermin || null })} />
          </div>
          <button onClick={onBestellen} disabled={busy}
                  className="ml-auto bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50 whitespace-nowrap">
            {busy ? '…' : '→ An Einkauf weiterleiten (Bestellen)'}
          </button>
        </div>
      ) : (
        <div className="text-xs text-slate-500 mb-3">
          Preis: {row.preis != null ? `${row.preis} €` : '—'} · Liefertermin: {row.liefertermin || '—'}
        </div>
      )}
      <SonderteilDateien anfrageId={row.id} dateien={dateien} istEigene={istEigene} />
    </div>
  );
}

function SonderteilDateien({ anfrageId, dateien, istEigene }: {
  anfrageId: number; dateien: ReturnType<typeof useApi<{ data: TicketDatei[] }>>; istEigene: boolean;
}) {
  const [busy, setBusy] = useState(false);

  const hochladen = async (datei: File) => {
    setBusy(true);
    try { await api.sonderteile.dateiHochladen(anfrageId, datei); dateien.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const loeschen = async (name: string) => {
    if (!confirm(`"${name}" löschen?`)) return;
    try { await api.sonderteile.dateiLoeschen(anfrageId, name); dateien.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  const dateiList: TicketDatei[] = dateien.data?.data ?? [];

  return (
    <div className="border-t border-slate-100 pt-2">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-slate-500 whitespace-nowrap">📎 PDF / DXF:</label>
        {dateiList.map(d => (
          <span key={d.name} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2.5 py-1 text-xs">
            <a href={api.sonderteile.dateiUrl(anfrageId, d.name)} target="_blank" rel="noreferrer"
               className="text-brand hover:underline">{d.name}</a>
            {istEigene && <button onClick={() => void loeschen(d.name)} className="text-slate-400 hover:text-red-600">✕</button>}
          </span>
        ))}
        {istEigene && (
          <label className="text-xs text-brand hover:underline cursor-pointer">
            {busy ? 'Lädt hoch …' : '+ Datei hinzufügen'}
            <input type="file" accept=".pdf,.dxf,application/pdf" className="hidden" disabled={busy}
                   onChange={e => { const f = e.target.files?.[0]; if (f) void hochladen(f); e.target.value = ''; }} />
          </label>
        )}
      </div>
    </div>
  );
}
